"use client";

import { cn } from "@/lib/utils";
import { ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { ButtonPrimary } from "./ButtonPrimary";

interface Tab {
  label: string;
  onClick?: () => void;
  isActive?: boolean;
  href?: string;
}

interface BreadcrumbNavProps {
  tabs: Tab[];
  onBack?: () => void;
  hasBackButton?: boolean;
  href?: string;
}

export function NavigationTabs({
  tabs,
  onBack,
  hasBackButton = true,
  href,
}: BreadcrumbNavProps) {
  const BackButton = () => (
    <ButtonPrimary className="flex items-center gap-1 px-4 h-full rounded-l-full">
      <ChevronLeft className="w-6 h-6" />
      <span className="text-sm font-medium">Back</span>
    </ButtonPrimary>
  );
  return (
    <div className="flex items-center gap-[1px] text-white overflow-hidden w-fit h-[50px]">
      {/* Back Button */}
      {hasBackButton &&
        (onBack ? (
          <button onClick={onBack} className="h-full">
            <BackButton />
          </button>
        ) : href ? (
          <Link to={href} className="h-full">
            <BackButton />
          </Link>
        ) : null)}

      {/* Tabs */}
      <div className="flex items-center h-full gap-2">
        {tabs.map(({ label, onClick, isActive }, index) => {
          const isFirst = index === 0;
          const isLast = index === tabs.length - 1;
          const isMiddle = !isFirst && !isLast;

          let roundingStyle = {};
          if (isFirst && isLast) {
            // Single tab - always fully rounded
            roundingStyle = { borderRadius: "9999px" };
          } else if (isFirst) {
            // First tab - full rounding on left, 6px on right
            roundingStyle = {
              borderTopLeftRadius: "9999px",
              borderBottomLeftRadius: "9999px",
              borderTopRightRadius: "6px",
              borderBottomRightRadius: "6px",
            };
          } else if (isLast) {
            // Last tab - 6px on left, full rounding on right
            roundingStyle = {
              borderTopLeftRadius: "6px",
              borderBottomLeftRadius: "6px",
              borderTopRightRadius: "9999px",
              borderBottomRightRadius: "9999px",
            };
          } else if (isMiddle) {
            // Middle tabs - get 6px rounding on all sides
            roundingStyle = { borderRadius: "6px" };
          }

          return (
            <button
              key={index}
              onClick={onClick}
              style={roundingStyle}
              className={cn(
                "flex bg-secondary-background items-center px-4 text-sm text-foreground h-full whitespace-nowrap",
                isActive && "bg-primary text-primary-foreground",
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
